import {
  Component,
  OnDestroy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  HostListener,
} from '@angular/core';
// Ag-Grid
import { AgGridAngular } from 'ag-grid-angular';
import { AsyncPipe } from '@angular/common';
import { BsDropdownModule } from 'ngx-bootstrap/dropdown';
import {
  GridOptions,
  GridApi,
  ColDef,
  StatusPanelDef,
  CellContextMenuEvent,
  Theme,
  themeQuartz,
  colorSchemeDark,
  colorSchemeDarkWarm,
  RowSelectionOptions,
} from 'ag-grid-community';
import { agGridDarkTheme } from 'src/app/shared/components/ag-grid/dark-theme';
// Services
import { SharedService } from '../../shared/services/shared.service';
import { ContextMenuService } from '../../shared/services/context-menu.service';
import { AccountingService } from '../services/accounting.service';
import { AgGridService } from 'src/app/shared/services/ag-grid.service';
// Third Party
import { Subscription, of } from 'rxjs';
import { switchMap, catchError } from 'rxjs/operators';
import * as Papa from 'papaparse';
import * as ExcelJS from 'exceljs';
// components
import { EditDeleteBtnsComponent } from 'src/app/shared/components/edit-delete-btns/edit-delete-btns.component';
import { ProductsServicesTableColumnDef } from './columnDefs';
import { Expense, ProductServiceItem } from 'src/app/shared/models/accounting';
import {
  Answers,
  InventoryTypes,
  ModalType,
  Questions,
  Statements,
  ComponentNames,
  StatusModalIndicator,
  ModalID,
} from 'src/app/shared/models/constants';
import {
  getFflModelProperties,
  getProductServiceModel,
} from '../shared/functions';
import { LiveVendorData } from 'src/app/shared/models/vendor';
import { isObjectEmpty, isValidFileType } from 'src/app/shared/functions';

@Component({
  selector: 'app-products-services',
  templateUrl: './products-services.component.html',
  styleUrls: ['./products-services.component.scss'],
  standalone: true,
  imports: [AgGridAngular, AsyncPipe, BsDropdownModule],
})
export class ProductsServicesComponent {
  @ViewChild('grid', { read: ElementRef }) gridElement!: ElementRef;
  @ViewChild('grid') grid!: AgGridAngular; // Keep for grid API access

  subscriptions = new Subscription();
  products_services_filtered: ProductServiceItem[] = [];
  show_inactive_items: boolean = false;

  statusBar: {
    // enterprise functionality
    statusPanels: StatusPanelDef[];
  } = {
    statusPanels: [
      { statusPanel: 'agTotalAndFilteredRowCountComponent' },
      { statusPanel: 'agTotalRowCountComponent' },
      { statusPanel: 'agFilteredRowCountComponent' },
      { statusPanel: 'agSelectedRowCountComponent' },
      { statusPanel: 'agAggregationComponent' },
    ],
  };
  public rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
  };
  // ag-grid properties Grid
  gridApi!: GridApi;
  columnDefs: ColDef[] = ProductsServicesTableColumnDef;
  defaultColDef;
  gridOptions: GridOptions = {
    theme: agGridDarkTheme,
    suppressDragLeaveHidesColumns: true,
  };
  components: any;
  // Thumbnail / lightbox state
  itemThumbnailUrls: string[] = [];
  currentImageIndex = 0;
  isLightboxOpen = false;
  isThumbnailLoading = false;
  // Context Menu state from service
  menuState$ = this.contextMenuService.menuState$;
  theme = agGridDarkTheme;
  constructor(
    public sharedService: SharedService,
    private contextMenuService: ContextMenuService,
    public accountingService: AccountingService,
    private cdr: ChangeDetectorRef,
    private agGridService: AgGridService,
  ) {
    this.gridOptions = <GridOptions>{
      getRowStyle: (params) => {
        if (params.data.serial_item) {
          return { color: 'darkseagreen' };
        } else if (params.data.type === InventoryTypes.inventory) {
          return { color: 'coral' };
        } else if (params.data.type === InventoryTypes.service) {
          return { color: 'lightskyblue' };
        } else {
          return null;
        }
      },
    };
    this.gridOptions.headerHeight = 50;
    this.gridOptions.rowHeight = 28;
    this.gridOptions.enableCellTextSelection = true; // Allows text selection for copying
    this.gridOptions.ensureDomOrder = true; // Ensures proper DOM order for accessibility
    this.gridOptions.suppressClipboardPaste = false; // Allow clipboard operations
    this.gridOptions.context = { ConfigComponent: this }; // this allows access to component functions
    this.gridOptions.rowClass = 'ag-grid-row';
    this.defaultColDef = {
      sortable: true,
      filter: true,
      resizable: true,
      floatingFilter: true,
      wrapHeaderText: true,
    };
    this.components = {
      ViewEditBtns: EditDeleteBtnsComponent,
    };

    // product_service_retrieved$
    this.subscriptions.add(
      this.accountingService.product_service_retrieved$.subscribe(
        (products_services) => {
          this.accountingService.products_services = [
            ...(products_services || []),
          ];
          if (this.show_inactive_items) {
            this.showInactiveItems();
          } else {
            this.removeInactiveItems();
          }
          this.sharedService.closeStatusSpinner();
        },
      ),
    );
    // product_service_created$
    this.subscriptions.add(
      this.accountingService.product_service_created$.subscribe((new_item) => {
        this.accountingService.products_services = [
          ...this.accountingService.products_services,
        ];
      }),
    );
    // product_service_to_add$
    this.subscriptions.add(
      this.accountingService.product_service_to_add$.subscribe(
        (saveProductServiceResponse) => {
          console.error(saveProductServiceResponse.item);
          this.addItem(saveProductServiceResponse.item);
        },
      ),
    );
    // product_service_to_edit$
    this.subscriptions.add(
      this.accountingService.product_service_to_edit$.subscribe(
        (saveProductServiceResponse) => {
          this.editItem(saveProductServiceResponse.item);
        },
      ),
    );
    // product_service_added$
    this.subscriptions.add(
      this.accountingService.products_services_list_updated$.subscribe(
        (saveProductServiceResponse) => {
          this.accountingService.products_services = [
            ...this.accountingService.products_services,
          ];
          if (this.show_inactive_items) {
            this.showInactiveItems();
          } else {
            this.removeInactiveItems();
          }
        },
      ),
    );
    // file_selected_from_picker$
    this.subscriptions.add(
      this.sharedService.file_selected_from_picker$.subscribe((file_info) => {
        if (file_info.file) {
          if (isValidFileType(file_info.file, ['csv', 'xlsx'])) {
            this.verifyFile(file_info.file, 'product_service');
          } else {
            this.sharedService.openMessageModal(
              Statements.incorrect_file_type_csv_xlsx,
            );
          }
        }
      }),
    );
    // file has been verified for import
    this.subscriptions.add(
      this.sharedService.file_verified$.subscribe(({ file, import_type }) => {
        const file_type = file.name.split('.').pop()?.toLowerCase();
        this.importProductServiceFile(file, file_type);
      }),
    );
    // yes_no_submit$ - product/service_to_delete
    this.subscriptions.add(
      this.sharedService.yes_no_submit$.subscribe((response) => {
        if (
          response['answer'] === Answers.yes &&
          (response['question'] === Questions.delete_product ||
            response['question'] === Questions.delete_service)
        ) {
          this.accountingService.deleteProductService(
            response['optional_details'] as ProductServiceItem,
          );
        } else {
          console.log('No action taken');
        }
      }),
    );
    // categories_selected_for_import$
    this.subscriptions.add(
      this.accountingService.categories_selected_for_import$.subscribe(
        (selected_categories: string[]) => {
          console.warn(selected_categories);
          this.importSelectedLiveInventoryItems(selected_categories);
        },
      ),
    );
  }

  async onGridReady(params: any) {
    this.gridApi = params.api;

    if (!this.agGridService.hasCachedSettingsFor(ComponentNames.product_services)) {
      await this.agGridService.loadSavedTableSettings(
        ComponentNames.product_services,
      );
    }
    this.applyCurrentSessionPreferences();

    // When qty_sums updates (inventory_lots change-stream tick), force the
    // Qty on Hand column to recompute. ag-grid otherwise has no way to know
    // that the external map this cell reads from has changed.
    this.subscriptions.add(
      this.accountingService.qty_sums_updated$.subscribe(() => {
        this.gridApi?.refreshCells({ columns: ['qty'], force: true });
      }),
    );
  }
  async ngOnInit() {
    this.addButtonColumn();
    if (this.accountingService.products_services.length === 0) {
      this.sharedService.openStatusSpinner('Loading Products and Services');
      await this.accountingService.getProductsServices();
      this.sharedService.closeStatusSpinner();
    }
    this.removeInactiveItems();
    this.getDistinctCategoryList();
  }
  ngAfterViewInit() {
    this.contextMenuService.initialize(this.cdr, this.gridElement);
  }
  addButtonColumn() {
    const hasEditDelete = this.columnDefs.some(
      (column) => column.headerName === 'View / Edit',
    );
    if (!hasEditDelete) {
      this.columnDefs.push({
        // View / Edit
        headerName: 'View / Edit',
        field: 'view_edit',
        width: 100,
        minWidth: 100,
        pinned: 'right',
        suppressMovable: true, // make not movable
        cellRenderer: 'ViewEditBtns',
        cellRendererParams: {
          editItem: this.editItemClick.bind(this),
          deleteItem: this.deleteItemClick.bind(this),
        },
      });
    }
  }
  async refreshTableDataClick() {
    this.sharedService.openStatusSpinner(
      'Refreshing Product and Service Records',
    );
    await this.accountingService.getProductsServices();
    this.removeInactiveItems();
    this.getDistinctCategoryList();
    this.sharedService.closeStatusSpinner();
  }
  editItemClick(selected_row: {
    colValues: ProductServiceItem;
    rowIndex: number;
  }) {
    const item = selected_row.colValues;
    this.sharedService.openCreateProductService(ModalType.edit, item);
  }
  deleteItemClick(selected_row: {
    colValues: ProductServiceItem;
    rowIndex: number;
  }) {
    const item = selected_row.colValues;
    if (selected_row.colValues.type === InventoryTypes.service) {
      this.sharedService.openYesNoModal(Questions.delete_service, item);
    } else {
      this.sharedService.openYesNoModal(Questions.delete_product, item);
    }
  }
  createNewService() {
    const item = new ProductServiceItem();
    item.type = InventoryTypes.service;
    this.sharedService.openCreateProductService(ModalType.add, item);
  }
  createNewInventoryItem() {
    const item = new ProductServiceItem();
    item.type = InventoryTypes.inventory;
    this.sharedService.openCreateProductService(ModalType.add, item);
  }

  editItem(item: ProductServiceItem) {
    this.accountingService.editProductService(item);
  }

  async addItem(item: ProductServiceItem) {
    await this.accountingService.addNewProductService(item);
  }
  deleteOrArchiveItem(item: ProductServiceItem) {
    // if (this.accountingService.sales_records.some((record) => record.sales_items === item.id)) {
    //   this.sharedService.openSnackBar('Cannot delete item with sales records', 'warning');
    //   return;
    // }
    // this.accountingService.deleteProductService(item);
  }

  showInactiveItemsClick() {
    this.show_inactive_items = !this.show_inactive_items;
    this.products_services_filtered = JSON.parse(
      JSON.stringify(this.accountingService.products_services),
    ).filter((item) => item.archived === false);
    this.products_services_filtered = [...this.products_services_filtered];
  }
  removeInactiveItemsClick() {
    this.show_inactive_items = !this.show_inactive_items;
    this.products_services_filtered = JSON.parse(
      JSON.stringify(this.accountingService.products_services),
    ).filter((item) => item.active === true && item.archived === false);
    this.products_services_filtered = [...this.products_services_filtered];
  }
  showInactiveItems() {
    this.products_services_filtered = JSON.parse(
      JSON.stringify(this.accountingService.products_services),
    ).filter((item) => item.archived === false);
    this.products_services_filtered = [...this.products_services_filtered];
  }
  removeInactiveItems() {
    this.products_services_filtered = JSON.parse(
      JSON.stringify(this.accountingService.products_services),
    ).filter((item) => item.active === true && item.archived === false);
    this.products_services_filtered = [...this.products_services_filtered];
  }
  downloadProductServiceTemplate() {
    const product_service: string[] = getProductServiceModel();
    const file_name = 'Product_Service_Import_Template';
    this.accountingService.buildAndOpenExcelFile(product_service, file_name);
  }
  getDistinctCategoryList() {
    this.sharedService
      .getAllDistinctCategories()
      .subscribe((response: any[]) => {
        this.accountingService.category_list = response.filter(
          (category) =>
            category['category'] !== null && category['category'] !== '',
        );
        this.sortCategoryList();
      });
  }
  sortCategoryList(): void {
    this.accountingService.category_list.sort((a, b) => {
      if (a['category'] < b['category']) {
        return -1;
      }
      if (a['category'] > b['category']) {
        return 1;
      }
      return 0;
    });
  }
  importProductServiceFile(file: File, file_type: string) {
    if (file_type === 'xlsx') {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const buffer = event.target?.result as ArrayBuffer;
        const workbook = new ExcelJS.Workbook();
        await workbook.xlsx.load(buffer);
        const worksheet = workbook.worksheets[0];
        const headers: string[] = [];
        worksheet.getRow(1).eachCell((cell) => { headers.push(String(cell.value ?? '')); });
        const product_service_import_list: any[] = [];
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber === 1) return;
          const obj: any = {};
          row.eachCell((cell, colNumber) => { obj[headers[colNumber - 1]] = cell.value; });
          product_service_import_list.push(obj);
        });
        let products_services: ProductServiceItem[] = [];

        for (let i = 0; i < product_service_import_list.length; i++) {
          const product_service = product_service_import_list[i];
          let new_products_services: ProductServiceItem =
            new ProductServiceItem();

          new_products_services.name = product_service['name']
            ? product_service['name']
            : null;
          new_products_services.upc = product_service['upc']
            ? product_service['upc']
            : null;
          new_products_services.type = product_service['type']
            ? product_service['type']
            : null;
          new_products_services.category = product_service['category']
            ? product_service['category']
            : null;
          new_products_services.description = product_service['description']
            ? product_service['description']
            : null;
          new_products_services.serial_item = product_service['serial_item']
            ? product_service['serial_item']
            : null;
          new_products_services.sale_price_rate = product_service[
            'sale_price_rate'
          ]
            ? product_service['sale_price_rate']
            : null;
          new_products_services.cost = product_service['cost']
            ? product_service['cost']
            : null;
          new_products_services.income_asset_account = product_service[
            'income_asset_account'
          ]
            ? product_service['income_asset_account']
            : null;
          new_products_services.income_account = product_service[
            'income_account'
          ]
            ? product_service['income_account']
            : null;
          new_products_services.expense_account = product_service[
            'expense_account'
          ]
            ? product_service['expense_account']
            : null;
          new_products_services.inventory_asset_account = product_service[
            'inventory_asset_account'
          ]
            ? product_service['inventory_asset_account']
            : null;
          new_products_services.reorder_point = product_service['reorder_point']
            ? product_service['reorder_point']
            : null;
          new_products_services.sales_tax = product_service['sales_tax']
            ? product_service['sales_tax']
            : null;
          new_products_services.preferred_vendor = product_service[
            'preferred_vendor'
          ]
            ? product_service['preferred_vendor']
            : null;
          new_products_services.active = true;
          new_products_services.archived = false;

          products_services.push(new_products_services);
        }
        if (products_services.length === 0) {
          this.sharedService.raiseWarningToast(
            'No products or services found in file.',
          );
          return;
        } else {
          this.accountingService.addMultipleProductService(products_services);
        }
      };
      reader.readAsArrayBuffer(file);
    } else if (file_type === 'csv') {
      Papa.parse(file, {
        header: true,
        complete: (results) => {
          const products_services = results.data;
          console.log(products_services);
          //this.accountingService.addVendors(vendors);
        },
        error: (error) => {
          console.error('Error parsing CSV file:', error);
        },
      });
    }
  }
  importSelectedLiveInventoryItems(selected_categories: string[]) {
    this.sharedService
      .getVendorInventoryBySelectedCategory(selected_categories)
      .subscribe((items_per_category) => {
        this.import_multiple_products_services(
          items_per_category as LiveVendorData[],
        );
      });
  }

  import_multiple_products_services(items_to_import: LiveVendorData[]) {
    this.accountingService.importProductsServicesBulk(items_to_import);
  }

  getLiveInventoryItems() {
    this.sharedService.getAllLiveInventoryData().subscribe((live_inventory) => {
      this.sharedService.all_live_vendor_data = [...live_inventory];
      console.log(this.sharedService.all_live_vendor_data[0]);
    });
  }

  getAllCategories() {
    this.sharedService.getAllDistinctCategories().subscribe((response) => {});
  }

  openImportVendorInventoryModalClick() {
    this.sharedService.openImportVendorInventoryModal();
  }
  verifyFile(file: File, file_format: string) {
    const product_service = getProductServiceModel();
    if (file_format === 'product_service') {
      const product_service_columns = [...product_service];
      if (isValidFileType(file, ['csv', 'xlsx'])) {
        if (file.name.split('.').pop()?.toLowerCase() === 'xlsx') {
          this.sharedService.validateXlsxColumns(
            file,
            product_service_columns,
            file_format,
          );
        } else {
          this.sharedService.validateCSVColumns(
            file,
            product_service_columns,
            file_format,
          );
        }
      } else {
        this.sharedService.openMessageModal(
          'Invalid file type. Please select a .csv or .xlsx file.',
        );
      }
    }
  }

  // preference functions
  storeCurrentSessionFilters() {
    this.agGridService.storeCurrentSessionFilters(
      ComponentNames.product_services,
      this.gridApi.getFilterModel(),
    );
  }
  storeCurrentSessionColumnConfig() {
    this.agGridService.storeCurrentSessionColumnConfig(
      ComponentNames.product_services,
      this.gridApi?.getColumnState(),
    );
  }
  applyCurrentSessionPreferences() {
    let component: any = this.agGridService.current_session_filtering.find(
      (component) =>
        component.component_name === ComponentNames.product_services,
    );
    if (component && !isObjectEmpty(component.filters)) {
      this.gridApi.setFilterModel(component.filters);
    }
    component = this.agGridService.current_session_column_config.find(
      (component) =>
        component.component_name === ComponentNames.product_services,
    );
    if (component && !isObjectEmpty(component.column_config)) {
      this.gridApi?.applyColumnState({
        state: component.column_config,
        applyOrder: true,
      });
    }
  }
  saveCurrentTablePreferencesClick() {
    const filters = this.gridApi.getFilterModel();
    const column_state = this.gridApi?.getColumnState();
    this.agGridService.saveCurrentTableSettings(
      ComponentNames.product_services,filters, column_state
    );
  }

  openPictureLightbox(rowData: any): void {
    if (!rowData) return;
    this.isThumbnailLoading = true;
    this.itemThumbnailUrls = [];
    this.currentImageIndex = 0;
    this.toggleLightbox(true);

    this.subscriptions.add(
      this.sharedService.getLiveInventoryItemByUpc(rowData.upc).pipe(
        switchMap((liveItem) =>
          this.sharedService.getInventoryThumbnailUrl(
            rowData.upc,
            liveItem?.item_number,
            liveItem?.manufacturer,
            liveItem?.mfg_part_number,
            liveItem?.origin,
            liveItem?.imageSource,
          )
        ),
      )
        .subscribe({
          next: (res) => {
            this.itemThumbnailUrls = res.thumbnailUrls || [];
            this.currentImageIndex = 0;
            this.isThumbnailLoading = false;
            if (this.itemThumbnailUrls.length === 0) {
              this.toggleLightbox(false);
              this.sharedService.raiseInfoToast('No image found for this item');
            }
            this.cdr.detectChanges();
          },
          error: (err) => {
            console.warn('Failed to fetch thumbnail:', err);
            this.itemThumbnailUrls = [];
            this.isThumbnailLoading = false;
            this.toggleLightbox(false);
            this.sharedService.raiseErrorToast('Failed to fetch image');
          },
        }),
    );
  }

  toggleLightbox(open: boolean): void {
    this.isLightboxOpen = open;
    document.body.style.overflow = open ? 'hidden' : 'auto';
  }

  prevImage(event: Event): void {
    event.stopPropagation();
    if (this.itemThumbnailUrls.length > 0) {
      this.currentImageIndex =
        (this.currentImageIndex - 1 + this.itemThumbnailUrls.length) %
        this.itemThumbnailUrls.length;
    }
  }

  nextImage(event: Event): void {
    event.stopPropagation();
    if (this.itemThumbnailUrls.length > 0) {
      this.currentImageIndex =
        (this.currentImageIndex + 1) % this.itemThumbnailUrls.length;
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent): void {
    if (this.isLightboxOpen) {
      if (event.key === 'Escape') this.toggleLightbox(false);
      if (event.key === 'ArrowLeft') this.prevImage(event);
      if (event.key === 'ArrowRight') this.nextImage(event);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
  // right click copy functions
  onCellContextMenu(event: CellContextMenuEvent) {
    this.contextMenuService.onCellContextMenu(event);
  }
  onMenuMouseEnter() {
    this.contextMenuService.onMenuMouseEnter();
  }
  onMenuMouseLeave() {
    this.contextMenuService.onMenuMouseLeave();
  }
  copyCellValue() {
    this.contextMenuService.copyCellValue();
  }
}

